import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link, useSearchParams } from 'react-router-dom';
import { api } from '../api.js';

export default function UnitDetailPage() {
  const navigate = useNavigate();
  const { studentUsername, unitCode } = useParams();
  const [searchParams] = useSearchParams();
  const optionalIDFromUrl = searchParams.get('optionalID');
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedAttemptIdx, setSelectedAttemptIdx] = useState(0);
  const [showReview, setShowReview] = useState(false);
  const [fullAttempts, setFullAttempts] = useState([]);

  useEffect(() => {
    let cancelled = false;
    api.getSubmissions().then(data => {
      if (cancelled) return;
      let studentSubmission = null;
      const allSubs = data.submissions || [];
      
      // Try optionalID first (handles duplicate usernames)
      if (optionalIDFromUrl) {
        for (const s of allSubs) {
          if (String(s.optionalID || '').trim() === optionalIDFromUrl.trim()) {
            studentSubmission = s;
            break;
          }
        }
      }
      
      // Fallback to username match
      if (!studentSubmission) {
        for (const s of allSubs) {
          if (s.username === studentUsername) {
            studentSubmission = s;
            break;
          }
        }
      }

      if (!studentSubmission) {
        setSubmissions([]);
        setLoading(false);
        return;
      }

      const unitAttempts = (studentSubmission.attempts || [])
        .filter(a => a.unitCode === unitCode)
        .sort((a, b) => a.attemptNumber - b.attemptNumber);

      setSubmissions([{ ...studentSubmission, filteredAttempts: unitAttempts }]);
      setLoading(false);
    }).catch(() => {
      if (cancelled) return;
      setSubmissions([]);
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, [studentUsername, unitCode, optionalIDFromUrl]);

  useEffect(() => {
    if (!submissions[0]) return;

    const course = submissions[0].course;
    if (!course) return;

    api.getUnitAttempts(course, unitCode, optionalIDFromUrl).then(data => {
      setFullAttempts(data.attempts || []);
    }).catch(() => {
      setFullAttempts([]);
    });
  }, [submissions[0]]);

  if (loading) {
    return <div style={{ padding: 40, textAlign: 'center', color: '#5b6776' }}>Loading...</div>;
  }

  const submission = submissions[0];
  const attempts = submission?.filteredAttempts || [];

  if (!submission || attempts.length === 0) {
    return (
      <div>
        <div style={{ marginBottom: 16, fontSize: 13 }}>
          <Link to="/students" style={{ color: 'var(--brand)', textDecoration: 'none' }}>Students</Link>
          {' > '}<span style={{ color: '#5b6776' }}>{submission.name || studentUsername}</span>
        </div>
        <h2>No submissions found</h2>
        <p style={{ color: '#5b6776' }}>No attempts found for unit {unitCode}.</p>
      </div>
    );
  }

 const attempt = attempts[Math.min(selectedAttemptIdx, Math.max(0, attempts.length - 1))] ?? null;
   const score = attempt?.score;

   // Map full attempts by attemptNumber so we can look up answers
   const fullByNumber = {};
   for (const fa of fullAttempts) {
     if (fa.attemptNumber != null) {
       fullByNumber[fa.attemptNumber] = fa;
     }
   }

   async function handleDownloadPDF(submission, attemptNum, submittedAt) {
     const params = new URLSearchParams({
       course: submission.course || '',
       unitCode,
       optionalID: submission.optionalID || optionalIDFromUrl || '',
       attempt: String(attemptNum),
     });
     const token = sessionStorage.getItem('ibma_admin_token');
     try {
       const res = await fetch(`/.netlify/functions/generate-submission-pdf?${params.toString()}`, {
         headers: { Authorization: `Bearer ${token}` },
       });
       if (!res.ok) throw new Error('Failed to generate PDF');
       const blob = await res.blob();
       const url = URL.createObjectURL(blob);
       const a = document.createElement('a');
       a.href = url;
       
       // Format submitted date as DD.MM.YY
       let dateStr = 'unknown';
       if (submittedAt) {
         try {
           const d = new Date(submittedAt);
           if (!isNaN(d.getTime())) {
             const dd = String(d.getDate()).padStart(2, '0');
             const mm = String(d.getMonth() + 1).padStart(2, '0');
             const yy = String(d.getFullYear()).slice(-2);
             dateStr = `${dd}.${mm}.${yy}`;
           }
         } catch {}
       }
       
       const studentName = (submission.name || studentUsername || 'unknown').replace(/[^a-zA-Z0-9\s]/g, '').trim().replace(/\s+/g, '_');
       const optId = (submission.optionalID || optionalIDFromUrl || 'unknown').replace(/[^a-zA-Z0-9]/g, '');
       
       a.download = `${unitCode}_${attemptNum}_${studentName}_${optId}_${dateStr}.pdf`;
       document.body.appendChild(a);
       a.click();
       document.body.removeChild(a);
       URL.revokeObjectURL(url);
     } catch {
       alert('Failed to generate PDF. Please try again.');
     }
   }

  return (
    <div>
      {/* Breadcrumb */}
      <div style={{ marginBottom: 16, fontSize: 13 }}>
        <Link to="/students" style={{ color: 'var(--brand)', textDecoration: 'none' }}>Students</Link>
        {' > '}<span style={{ color: '#5b6776' }}>{submission.name || studentUsername}</span>
      </div>

      {/* Header */}
      <h2 style={{ marginTop: 0, marginBottom: 4 }}>{unitCode}</h2>
      <p style={{ margin: '0 0 20px', color: '#5b6776', fontSize: 14 }}>
        {submission.name || studentUsername} — Attempt {attempt.attemptNumber || attempt.attempt} of {attempts.length}
      </p>

      {/* Score Box */}
      <div className="card" style={{ padding: 20, marginBottom: 24 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div style={{ fontSize: 12, color: '#5b6776', marginBottom: 4 }}>Score</div>
            <div className="score-number" style={{ fontSize: 32, fontWeight: 700, color: score?.percent === 100 ? '#27ae60' : '#c0392b' }}>
              {score?.correct ?? '—'} / {score?.total ?? '—'}
            </div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: 12, color: '#5b6776', marginBottom: 4 }}>Percentage</div>
            <div className="score-percent" style={{ fontSize: 32, fontWeight: 700, color: score?.percent === 100 ? '#27ae60' : '#c0392b' }}>
              {score?.percent != null ? `${score.percent}%` : '—'}
            </div>
          </div>
        </div>
      </div>

      {/* Segmented button: toggle review + download PDF */}
      <div style={{ display: 'inline-flex', marginBottom: 20, borderRadius: 4, overflow: 'hidden', border: `1px solid var(--brand)` }}>
        <button
          onClick={() => setShowReview(prev => !prev)}
          style={{
            background: showReview ? 'var(--brand)' : '#f0f4f8',
            color: showReview ? 'white' : 'var(--brand)',
            border: 'none',
            borderRadius: 0,
            padding: '10px 20px',
            cursor: 'pointer',
            fontSize: 14,
            fontWeight: 600,
            whiteSpace: 'nowrap',
            transition: 'all 0.15s'
          }}
        >
          {showReview ? `Hide Attempt ${attempt.attemptNumber || attempt.attempt}` : `View Attempt ${attempt.attemptNumber || attempt.attempt}`}
        </button>
        <button
          onClick={() => handleDownloadPDF(submission, attempt.attemptNumber || attempt.attempt, attempt.submittedAt)}
          style={{
            background: showReview ? '#1a5f9e' : 'var(--brand)',
            color: 'white',
            borderLeft: `1px solid var(--brand)`,
            borderRadius: 0,
            padding: '10px 12px',
            cursor: 'pointer',
            fontSize: 18,
            fontWeight: 600,
            whiteSpace: 'nowrap',
            transition: 'all 0.15s'
          }}
        >
          📄
        </button>
      </div>

      {/* Attempt Tabs */}
      {attempts.length > 1 && (
        <div style={{ marginBottom: 20, display: 'flex', gap: 8 }}>
          {attempts.map((a, i) => (
            <button
              key={i}
              onClick={() => setSelectedAttemptIdx(i)}
              style={{
                background: selectedAttemptIdx === i ? '#f0f4f8' : 'transparent',
                border: `1px solid ${selectedAttemptIdx === i ? 'var(--brand)' : 'var(--border)'}`,
                padding: '6px 16px', cursor: 'pointer', fontSize: 13, fontWeight: selectedAttemptIdx === i ? 600 : 400,
                color: selectedAttemptIdx === i ? 'var(--brand)' : '#5b6776', borderRadius: 4, transition: 'all 0.15s'
              }}
            >
              Attempt {a.attemptNumber || a.attempt}
            </button>
          ))}
        </div>
      )}

      {/* Review content */}
      {showReview && (
        <>
          <h3 style={{ marginTop: 24 }}>Review</h3>
          {(() => {
            const fullAttempt = fullByNumber[attempt.attemptNumber] || {};
            const answers = fullAttempt.answers || [];
            
            if (answers.length === 0) {
              return <p style={{ color: '#5b6776' }}>No answer data available for this attempt.</p>;
            }

            return answers.map((a, idx) => {
              const options = a.options || {};
              return (
                <div className={`question ${a.isCorrect ? 'review-correct' : 'review-wrong'}`} key={idx}>
                  <h3>
                    {idx + 1}. {a.question || `Question ${idx + 1}`}{' '}
                    <span className={a.isCorrect ? 'badge badge-ok' : 'badge badge-bad'}>
                      {a.isCorrect ? 'Correct' : 'Incorrect'}
                    </span>
                  </h3>
                  {Object.entries(options).map(([key, text]) => {
                    const isChosen = a.chosen === key;
                    const isAnswer = a.correctAnswer === key;
                    let cls = 'option review-option';
                    if (isAnswer) cls += ' option-correct';
                    if (isChosen && !isAnswer) cls += ' option-wrong';
                    return (
                      <div className={cls} key={key}>
                        <strong>{key}.</strong> {text}
                        {isAnswer && <span className="hint"> correct answer</span>}
                        {isChosen && !isAnswer && <span className="hint"> your answer</span>}
                      </div>
                    );
                  })}
                  {a.evidence && a.evidence.length > 0 && (
                    <div style={{ marginTop: 8, padding: '8px 12px', background: '#f8f9fb', borderRadius: 4, fontSize: 13, color: '#5b6776' }}>
                      <strong>Evidence:</strong> {a.evidence.join(', ')}
                    </div>
                  )}
                </div>
              );
            });
          })()}

          {/* Back Button */}
          <div style={{ marginTop: 20 }}>
            <button onClick={() => navigate(-1)} style={{ background: 'transparent', color: 'var(--brand)', padding: '8px 0' }}>
              ← Back
            </button>
          </div>
        </>
      )}

      {!showReview && (
        <div style={{ marginTop: 20 }}>
          <button onClick={() => navigate(-1)} style={{ background: 'transparent', color: 'var(--brand)', padding: '8px 0' }}>
            ← Back
          </button>
        </div>
      )}
    </div>
  );
}
